import './WYSIWYG.sass';
import 'react-quill/dist/quill.snow.css';
import 'katex/dist/katex.min.css';

import katex from 'katex';
import PropTypes from 'prop-types';
import QuillImageDropAndPaste from 'quill-image-drop-and-paste';
import ImageResize from 'quill-image-resize-module--fix-imports-error';
import React from 'react';
import ReactQuill, { Quill } from 'react-quill';

import * as api from '../../utilities/ServiceManager';

window.Quill = Quill;
window.katex = katex;

const icons = Quill.import('ui/icons');
icons.undo = `<svg viewbox="0 0 18 18">
<polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
<path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
</svg>`;
icons.redo = `<svg viewbox="0 0 18 18">
<polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
<path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
</svg>`;

Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste);
Quill.register('modules/imageResize', ImageResize);
// eslint-disable-next-line
Quill.register('modules/disableTab', function (quill, options) {
  // Disable /t char insert in tab press
  delete quill.getModule('keyboard').bindings['9'];

  // Disable tab navigation for toolbar icons
  // The query below matches all toolbar classes like ql-icon-picker, ql-bold, ql-italic, etc.
  const toolbarButtons = document.querySelectorAll(
    '[class^="ql-"]:not(.ql-editor)',
  );
  toolbarButtons.forEach((item) => {
    item.setAttribute('tabindex', -1);
  });
});

export default class WYSIWYG extends React.Component {
  constructor() {
    super();

    this.handleModelChange = this.handleModelChange.bind(this);
    this.state = {
      model: '<p></p>',
    };

    this.reactQuillRef = React.createRef();
  }

  UNSAFE_componentWillMount() {
    if (this.props.value) {
      this.setState({
        model: this.props.value,
      });
    }
  }

  componentDidMount() {
    const editor = this.reactQuillRef?.current?.getEditor();
    if (editor) {
      // Disable the browser's native spell check so it doesn't draw its own
      // (different-dictionary) underlines on top of the custom spell-check preview.
      editor.root.setAttribute('spellcheck', 'false');
      if (this.props.onImagePaste) {
        editor.root.addEventListener('paste', this.handleCustomPaste, true);
      }
    }
  }

  componentWillUnmount() {
    const editor = this.reactQuillRef?.current?.getEditor();
    if (editor && this.props.onImagePaste) {
      editor.root.removeEventListener('paste', this.handleCustomPaste, true);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.value !== nextProps.value &&
      nextProps.value &&
      nextProps.updateEditorView !== false
    ) {
      this.setState({
        model: nextProps.value,
      });
    }
    if (!nextProps.value && nextProps.updateEditorView) {
      this.setState({
        model: '<p></p>',
      });
    }
  }

  handleModelChange = async (model) => {
    if (model.includes('data:image')) {
      // it's not an s3/digitalOcean link, we need to upload it
      model = await this.uploadBase64Images(model);
      const editor = this.reactQuillRef?.current?.getEditor();
      // model is just previous model but with the image link of the cloud instead of base64
      // so there is no "danger"
      editor.clipboard.dangerouslyPasteHTML(model);
      return;
    }

    this.setState({
      model,
    });
    if (this.props.redBorder && model.length === 0) {
      this.setState({
        model: '<p></p>',
      });
    }

    // Empty model is <p><br></p> or <p></p> by default
    // So we need to set it to empty string
    // To make validation work
    if (model === '<p><br></p>' || model === '<p></p>') {
      model = '';
    }

    if (this.props.onChange) {
      this.props.onChange(model);
    }
  };

  uploadImageCallBack = (file) => {
    const data = new FormData();
    data.append('file', file);
    return new Promise((resolve, reject) => {
      api
        .uploadFile(data)
        .then((d) => {
          resolve({
            data: {
              link: d.path,
            },
          });
        })
        .catch((err) => {
          if (this.props.showWarning) {
            this.props.showWarning(
              'Input error!',
              'Error uploading image. Please try again later.',
            );
          }
          reject(err);
        });
    });
  };

  uploadImage(file) {
    this.uploadImageCallBack(file)
      .then((res) => {
        if (res.data.link) {
          if (this.props.onImagePaste) {
            this.props.onImagePaste(res.data.link);
            return;
          }
          const editor = this.reactQuillRef?.current?.getEditor();
          const range = editor.getSelection();
          editor.insertEmbed(
            range.index,
            'image',
            res.data.link,
            Quill.sources.USER,
          );
        } else {
          // eslint-disable-next-line
          console.error(res.data);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line
        console.error(e);
      });
  }

  // when user clicks in upload image
  imageHandlerButton = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = () => {
      this.uploadImage(input.files[0]);
    };
  };

  // when user drags and drops image
  imageHandlerDrop = (imageDataUrl, type, imageData) => {
    const file = imageData.toFile();
    this.uploadImage(file);
  };

  handleCustomPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.match(/^image\/(gif|jpe?g|a?png|svg|webp|bmp)/i)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const file = items[i].getAsFile();
        if (file) {
          this.uploadImage(file);
        }
        return;
      }
    }
  };

  myUndo = () => this?.reactQuillRef?.current?.getEditor()?.history?.undo();

  myRedo = () => this?.reactQuillRef?.current?.getEditor()?.history?.redo();

  // this is used to convert base64 images into s3/digitalOcean links. it uploads it to the cloud
  uploadBase64Images = async (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = Array.from(doc.querySelectorAll('img[src^="data:image"]'));

    await Promise.all(
      images.map(async (img) => {
        const base64 = img.src;
        const mimeType = base64.match(/:(.*?);/)[1];
        const byteString = atob(base64.split(',')[1]);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: mimeType });
        const file = new File([blob], 'image.png', { type: mimeType });
        const res = await this.uploadImageCallBack(file);
        img.src = res.data.link;
      }),
    );

    return doc.body.innerHTML;
  };

  render() {
    const { modalSize, contentError = false } = this.props;

    const handleKeyDownFull = (e) => {
      if (e.ctrlKey && !e.shiftKey && e.keyCode === 13) {
        const btnSaveCreate =
          document.getElementsByClassName('btn-save-create')[0];
        if (btnSaveCreate) {
          btnSaveCreate.click();
          btnSaveCreate.disabled = true;

          const questionInput = document.getElementsByClassName(
            'TextArea BorderTextArea form-control',
          );
          if (questionInput && questionInput.length > 0) {
            questionInput[0].focus();
          }
        }
      }
      if (e.ctrlKey && e.shiftKey && e.keyCode === 13) {
        const btnSaveDefault =
          document.getElementsByClassName('btn-save-default')[0];
        if (btnSaveDefault) {
          btnSaveDefault.click();
          btnSaveDefault.disabled = true;
          const questionInput = document.getElementsByClassName(
            'TextArea BorderTextArea form-control',
          );
          if (questionInput && questionInput.length > 0) {
            questionInput[0].focus();
          }
        }
      }
    };

    const handleKeyUpFull = () => {
      const btnSaveCreate =
        document.getElementsByClassName('btn-save-create')[0];
      if (btnSaveCreate) {
        btnSaveCreate.disabled = false;
      }
      const btnSaveDefault =
        document.getElementsByClassName('btn-save-default')[0];
      if (btnSaveDefault) {
        btnSaveDefault.disabled = false;
      }
    };

    const quillFormats = [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'video',
      'width',
      'image',
      'script',
      'align',
      'color',
      'background',
      'direction',
      'formula',
    ];

    const getToolbarOptions = () => {
      if (this.props.allowedFormats) {
        // If allowedFormats is provided, only show those options
        return [...this.props.allowedFormats, ['clean'], ['undo', 'redo']];
      }
      // Default full toolbar options
      return [
        [{ font: [] }],
        [{ size: [] }],
        [{ align: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [
          { list: 'ordered' },
          { list: 'bullet' },
          { indent: '-1' },
          { indent: '+1' },
        ],
        ['link', 'image', 'video', 'formula'],
        ['clean'],
        ['undo', 'redo'],
      ];
    };

    const modulesFull = {
      toolbar: {
        container: getToolbarOptions(),
        handlers: {
          image: this.imageHandlerButton,
          undo: this.myUndo,
          redo: this.myRedo,
        },
      },
      history: {
        delay: 100,
        maxStack: 100,
        userOnly: false,
      },
      imageDropAndPaste: {
        handler: this.props.allowedFormats ? null : this.imageHandlerDrop, // enable drop and paste only for further reading
      },
      imageResize: {
        // modules: ['Resize', 'DisplaySize', 'Toolbar'],
        modules: ['Resize', 'DisplaySize'],
      },
      clipboard: {
        matchVisual: false,
      },
      disableTab: true,
    };
    return (
      <>
        <div id="editor">
          <ReactQuill
            theme="snow"
            value={this.state.model}
            onChange={this.handleModelChange}
            modules={modulesFull}
            formats={quillFormats}
            ref={this.reactQuillRef}
            className={`${modalSize ? 'full-editor-125' : 'full-editor-350'}${
              contentError ? ' contentErrorBackground' : ''
            }`}
            onKeyDown={handleKeyDownFull}
            onKeyUp={handleKeyUpFull}
          />
        </div>
      </>
    );
  }
}

WYSIWYG.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  updateEditorView: PropTypes.bool,
  modalSize: PropTypes.bool,
  id: PropTypes.string,
  allowedFormats: PropTypes.arrayOf(PropTypes.string),
  showWarning: PropTypes.func,
  contentError: PropTypes.bool,
  onImagePaste: PropTypes.func,
};
